import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';

export type ThemePreference = 'system' | 'light';
export type ResolvedTheme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly preferences: ThemePreference[] = ['system', 'light'];
  readonly preference = signal<ThemePreference>(this.readPreference());

  private readonly document = inject(DOCUMENT);
  private readonly storageKey = 'lumen-hospitality-theme-preference';
  private readonly systemTheme = signal<ResolvedTheme>(this.readSystemTheme());

  readonly resolvedTheme = computed<ResolvedTheme>(() =>
    this.preference() === 'light' ? 'light' : this.systemTheme()
  );
  readonly nextPreference = computed<ThemePreference>(() =>
    this.preference() === 'system' ? 'light' : 'system'
  );

  constructor() {
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (event: MediaQueryListEvent) => {
        this.systemTheme.set(event.matches ? 'dark' : 'light');
      };

      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', handleChange);
      } else {
        mediaQuery.addListener(handleChange);
      }
    }

    effect(() => {
      const preference = this.preference();
      const resolvedTheme = this.resolvedTheme();
      const root = this.document.documentElement;

      root.dataset['theme'] = resolvedTheme;
      root.dataset['themePreference'] = preference;
      root.style.colorScheme = resolvedTheme;

      this.persistPreference(preference);
    });
  }

  setPreference(preference: ThemePreference): void {
    this.preference.set(preference);
  }

  togglePreference(): void {
    this.preference.set(this.nextPreference());
  }

  labelKey(preference: ThemePreference): string {
    return `shell.theme.${preference}`;
  }

  private readPreference(): ThemePreference {
    if (typeof window === 'undefined') {
      return 'system';
    }

    try {
      return window.localStorage.getItem(this.storageKey) === 'light' ? 'light' : 'system';
    } catch {
      return 'system';
    }
  }

  private readSystemTheme(): ResolvedTheme {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return 'light';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private persistPreference(preference: ThemePreference): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      if (preference === 'system') {
        window.localStorage.removeItem(this.storageKey);
      } else {
        window.localStorage.setItem(this.storageKey, preference);
      }
    } catch {
      // Ignore storage failures and keep the in-memory preference.
    }
  }
}
